/**
 * @file time-picker/time-picker-dialog.tsx
 *
 * TimePickerDialog — modal container for TimePicker and TimeInput.
 * Reuses Dialog + DialogPortal primitives matching DatePickerDialog pattern.
 *
 * Layout:
 * ```
 *   ┌─────────────────────┐
 *   │  [Content]          │  ← TimePicker or TimeInput
 *   │─────────────────────│
 *   │ 🎹  [Cancel] [OK]   │  ← footer: modeToggle + spacer + dismiss + confirm
 *   └─────────────────────┘
 * ```
 *
 * Tokens from TimePickerDialog.kt (shared with DatePickerDialog):
 * - ContainerColor = SurfaceContainerHigh
 * - ContainerShape = CornerExtraLarge (28px)
 * - ContainerElevation = Level3
 *
 * Ported from `TimePickerDialog.kt` and `DatePickerDialog` pattern.
 *
 * @see docs/m3/date-and-time-pickers/time-pickers/TimePickerDialog.kt
 * @see packages/react/src/ui/date-time-pickers/date-picker/date-picker-dialog.tsx
 */

import * as RadixDialog from "@radix-ui/react-dialog";
import { m } from "motion/react";
import type * as React from "react";
import { cn } from "../../../lib/utils";
import { Dialog, DialogPortal } from "../../dialog";
import {
	PICKER_CONTENT_ANIM,
	PICKER_OVERLAY_ANIM,
} from "../shared/animation-tokens";
import type { TimePickerDialogProps } from "./time-picker.types";

/**
 * TimePickerDialog — modal dialog container for TimePicker / TimeInput.
 *
 * Reuses `<Dialog>` (Root) and `<DialogPortal>` (AnimatePresence + Portal)
 * while managing Overlay and Content to preserve MD3 Time Picker specs.
 *
 * @example Basic usage

 * ```tsx
 * const state = useTimePickerState({ initialHour: 7 });
 * const [open, setOpen] = React.useState(false);
 * const [mode, setMode] = React.useState<'dial' | 'input'>('dial');
 *
 * <TimePickerDialog
 *   open={open}
 *   onDismiss={() => setOpen(false)}
 *   modeToggleButton={
 *     <button onClick={() => setMode(m => m === 'dial' ? 'input' : 'dial')}>
 *       <span className="md-icon">{mode === 'dial' ? 'keyboard' : 'schedule'}</span>
 *     </button>
 *   }
 *   confirmButton={
 *     <Button variant="text" onClick={() => { handleConfirm(state); setOpen(false); }}>
 *       OK
 *     </Button>
 *   }
 *   dismissButton={
 *     <Button variant="text" onClick={() => setOpen(false)}>Cancel</Button>
 *   }
 * >
 *   {mode === 'dial' ? <TimePicker state={state} /> : <TimeInput state={state} />}
 * </TimePickerDialog>
 * ```
 */
export const TimePickerDialog: React.FC<TimePickerDialogProps> = ({
	open,
	onDismiss,
	confirmButton,
	dismissButton,
	modeToggleButton,
	title = "Select time",
	children,
	className,
}) => {
	return (
		<Dialog open={open} onOpenChange={(v) => !v && onDismiss()}>
			<DialogPortal open={open}>
				{/* Scrim */}
				<RadixDialog.Overlay asChild forceMount>
					<m.div
						key="time-picker-overlay"
						className="fixed inset-0 z-50 bg-m3-scrim/40"
						initial={PICKER_OVERLAY_ANIM.initial}
						animate={PICKER_OVERLAY_ANIM.animate}
						exit={PICKER_OVERLAY_ANIM.exit}
						onClick={onDismiss}
					/>
				</RadixDialog.Overlay>

				{/* Dialog panel */}
				<RadixDialog.Content
					asChild
					forceMount
					onEscapeKeyDown={onDismiss}
					onPointerDownOutside={(e) => e.preventDefault()} // scrim handles dismiss
					aria-modal
					aria-describedby={undefined}
				>
					<m.div
						key="time-picker-content"
						className={cn(
							// Positioning
							"fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
							// Layout
							"flex flex-col",
							// MD3 Time Picker container tokens
							"bg-m3-surface-container-high",
							"rounded-m3-xl",
							"overflow-hidden",
							"max-w-[calc(100vw-32px)] max-h-[calc(100dvh-32px)]",
							// Elevation Level 3
							"elevation-3",
							className,
						)}
						initial={PICKER_CONTENT_ANIM.initial}
						animate={PICKER_CONTENT_ANIM.animate}
						exit={PICKER_CONTENT_ANIM.exit}
					>
						{/* Accessible title (screen reader only) */}
						<RadixDialog.Title className="sr-only">{title}</RadixDialog.Title>

						{/* Content — TimePicker or TimeInput */}
						<div className="flex-1">{children}</div>

						{/* Footer: modeToggle | spacer | dismiss | confirm */}
						<div className="flex min-h-16 items-center gap-2 px-6 py-2">
							{/* Mode toggle (keyboard ↔ clock icon) — optional */}
							{modeToggleButton && (
								<div className="flex items-center">{modeToggleButton}</div>
							)}
							{/* Spacer */}
							<div className="flex-1" />
							{/* Action buttons */}
							{dismissButton}
							{confirmButton}
						</div>
					</m.div>
				</RadixDialog.Content>
			</DialogPortal>
		</Dialog>
	);
};

TimePickerDialog.displayName = "TimePickerDialog";
